import React, { useEffect, useState } from "react";
import { fetchHomestarsReviews } from "@/lib/api";
import { Star, Quotes, ArrowSquareOut, CheckCircle } from "@phosphor-icons/react";
import { HOMESTARS_URL } from "@/components/site/HomeStarsBadge";

function HsStars({ rating }) {
  return (
    <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={16}
          weight="fill"
          className={i < rating ? "text-amber-500" : "text-slate-300"}
        />
      ))}
    </div>
  );
}

function ReviewCard({ r, featured = false }) {
  return (
    <article
      className={`group relative border-2 ${
        featured ? "border-emerald-600 bg-emerald-50/40" : "border-slate-900 bg-white"
      } p-6 flex flex-col`}
      data-testid={`homestars-review-${r.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <HsStars rating={r.rating} />
        <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-emerald-700 inline-flex items-center gap-1 shrink-0">
          <CheckCircle size={12} weight="fill" /> Verified
        </span>
      </div>
      <h3 className="font-display font-bold text-base text-slate-900 mt-3 leading-snug">
        {r.title}
      </h3>
      <p className="mt-2 text-sm text-slate-700 leading-relaxed flex-1">
        <Quotes size={14} weight="fill" className="text-emerald-600 inline mr-1 -mt-1 align-baseline" />
        {r.quote}
      </p>
      <div className="mt-5 pt-4 border-t border-slate-200 flex items-center justify-between text-xs">
        <span className="font-bold text-slate-900">{r.name}</span>
        <span className="text-slate-500">{r.date}</span>
      </div>
    </article>
  );
}

export default function HomeStarsReviews() {
  const [data, setData] = useState({ reviews: [], summary: null });

  useEffect(() => {
    fetchHomestarsReviews()
      .then((d) => setData(d))
      .catch(() => setData({ reviews: [], summary: null }));
  }, []);

  const reviews = data.reviews || [];
  const summary = data.summary || { average_rating: 5.0, review_count: reviews.length };

  return (
    <section
      data-testid="homestars-reviews-section"
      id="homestars-reviews"
      className="bg-slate-50 border-b-2 border-slate-200"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20 lg:py-28">
        {/* Header band — HomeStars branded */}
        <div className="border-2 border-slate-900 bg-white">
          <div className="grid lg:grid-cols-12 items-center">
            {/* Green left rail */}
            <div className="lg:col-span-4 bg-[#16a34a] text-white p-7 sm:p-9">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 bg-white flex items-center justify-center">
                  <Star size={28} weight="fill" className="text-[#16a34a]" />
                </div>
                <div className="leading-tight">
                  <div className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-emerald-100">
                    Verified on
                  </div>
                  <div className="font-display font-black text-2xl">HomeStars</div>
                </div>
              </div>
              <div className="mt-7 flex items-baseline gap-3">
                <span className="font-display font-black text-6xl leading-none">
                  {summary.average_rating?.toFixed(1) ?? "5.0"}
                </span>
                <span className="font-bold text-emerald-100">/ 5</span>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <HsStars rating={5} />
                <span className="text-sm font-semibold">{summary.review_count || reviews.length} reviews</span>
              </div>
              <a
                href={HOMESTARS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="homestars-header-cta"
                className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-white text-emerald-700 font-bold text-xs uppercase tracking-[0.18em] hover:bg-emerald-50 transition-colors"
              >
                View on HomeStars
                <ArrowSquareOut size={14} weight="bold" />
              </a>
            </div>

            {/* Right side */}
            <div className="lg:col-span-8 p-7 sm:p-10">
              <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-emerald-700">
                Real reviews · Real customers
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-slate-900 mt-3 leading-tight">
                Toronto&apos;s most-loved bin rental on HomeStars.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
                Every review below is verified by HomeStars &mdash; the same homeowners and trades who count
                on the Troya Mini Bins team for fast, fair, on-time bin service.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
                <div className="flex items-center gap-2 text-slate-700">
                  <CheckCircle size={18} weight="fill" className="text-emerald-600" />
                  <span className="font-bold">100% 5-star rated</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <CheckCircle size={18} weight="fill" className="text-emerald-600" />
                  <span>Verified by HomeStars</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <CheckCircle size={18} weight="fill" className="text-emerald-600" />
                  <span>Active since 2023</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews grid */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((r, i) => (
            <ReviewCard key={r.id} r={r} featured={i === 0} />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-5 border-2 border-slate-900 bg-slate-900 text-white p-6 sm:p-8">
          <div>
            <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-emerald-400">
              Trusted by your neighbours
            </div>
            <div className="font-display font-bold text-lg sm:text-xl mt-1">
              Join our happy customers &mdash; get your quote in minutes.
            </div>
          </div>
          <div className="flex gap-3">
            <a
              href={HOMESTARS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 border-2 border-white text-white font-bold text-xs uppercase tracking-[0.18em] hover:bg-white hover:text-slate-900 transition-colors"
            >
              Read on HomeStars
              <ArrowSquareOut size={14} weight="bold" />
            </a>
            <a
              href="/book"
              data-testid="homestars-section-quote-cta"
              className="inline-flex items-center justify-center px-5 py-3 bg-sky-500 hover:bg-sky-600 text-white font-bold text-xs uppercase tracking-[0.18em] border-2 border-sky-500 hover:border-sky-600 transition-colors"
            >
              Book now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
